/**
 * Query key factory — centralised keys for TanStack Query cache.
 *
 * Convention:
 *   queryKeys.<entity>.<scope>(...params)
 *
 * Broader keys invalidate narrower ones:
 *   invalidateQueries({ queryKey: ['leads'] })  → all lead-related queries
 *   invalidateQueries({ queryKey: ['leads', 'column'] })  → all column queries
 */
export const queryKeys = {
  statuses: {
    all: ['statuses'],
  },

  leads: {
    all: ['leads'],
    column: (statusId, search, managerFilter, statusIn, lastContactDays) =>
      ['leads', 'column', { statusId, search, managerFilter, statusIn, lastContactDays }],
    detail: (leadId) => ['leads', 'detail', leadId],
    boardStats: () => ['leads', 'board-stats'],
    unreadStatus: () => ['leads', 'unread-status'],

    bloggers: (...filters) => ['leads', 'bloggers', ...filters],
  },

  reminders: {
    pending: () => ['reminders', 'pending'],
    byLead: (leadId) => ['reminders', 'lead', leadId],
  },

  messages: {
    byLead: (leadId) => ['messages', leadId],
  },

  admin: {
    users: () => ['admin', 'users'],
    teams: () => ['admin', 'teams'],
  },

  notifications: {
    all: ['notifications'],
    list: () => ['notifications', 'list'],
    byTab: (tab) => ['notifications', 'byTab', tab],
    byTabGrouped: (tab) => ['notifications', 'byTab', 'grouped', tab],
    unreadByLead: () => ['notifications', 'unread-by-lead'],
    inbox: (filters) => ['notifications', 'inbox', filters],
  },

  tasks: {
    all: ['tasks'],
    today: () => ['tasks', 'today'],
  },

  inbox: {
    all: ['inbox'],
    list: (filters) => ['inbox', 'list', filters],
  },

  monitoring: {
    all: ['monitoring'],
    detail: (leadId) => ['monitoring', leadId],
    list: (filters) => ['monitoringList', filters],
    listAll: ['monitoringList'],
    alertsCount: () => ['monitoringList', 'alertsCount'],
  },

  // Phase 93.6 — Round metrics UI tab query keys.
  // Consumed by frontend/src/api/roundMetrics.js (useRoundMetrics, useAddBonus,
  // useDeleteBonus). `detail(collabId)` is invalidated onSuccess of bonus
  // add/remove mutations so the Метрики tab refetches
  // GET /api/collaborations/:id/metrics without re-rendering the whole lead modal.
  collaborationMetrics: {
    all: ['collaborationMetrics'],
    detail: (collabId) => ['collaborationMetrics', collabId],
  },

  donorScreening: {
    all: ['screenDonors'],
    detail: (leadId) => ['screenDonors', leadId],
  },

  accounts: {
    all: ['accounts'],
    detail: (leadId) => ['accounts', leadId],
  },

  tags: {
    all: ['tags'],

    mine: ['tags', 'mine'],
  },

  contracts: {
    all: ['contracts'],
    list: (statusFilter) => ['contracts', 'list', statusFilter],
    detail: (id) => ['contracts', 'detail', id],
    byLead: (leadId) => ['contracts', 'byLead', leadId],
  },

  invoices: {
    all: () => ['invoices'],
    byContract: (contractId) => ['invoices', 'contract', contractId],
    detail: (id) => ['invoices', 'detail', id],
    adminList: (filters) => ['invoices', 'admin', filters],
    log: (invoiceId) => ['invoices', 'log', invoiceId],   // Phase 65 D-19
  },

  // Phase 101 — Pack query keys (W3).
  // - `byLead(leadId)`              → GET /api/packs?leadId=X — invalidated onSuccess of create/update
  // - `detail(packId)`              → GET /api/packs/:id (future use)
  // - `roundProgress(packId)`       → GET /api/packs/:id/round-progress — invalidated after ban-account
  packs: {
    all: ['packs'],
    byLead: (leadId) => ['packs', 'byLead', leadId],
    detail: (packId) => ['packs', 'detail', packId],
    roundProgress: (packId) => ['packs', 'roundProgress', packId],
  },

  // R7 Phase 5 — rounds monitoring query keys.
  // - `overview(filters)`        → GET /api/monitoring-overview — Мониторинг page cards
  // - `byPack(packId)`           → GET /api/packs/:packId/rounds — round history (DESC)
  // - `daily(packId, roundId)`   → GET /api/packs/:packId/rounds/:roundId/daily — window matrix
  // - `window(packId, {from,to})`→ GET /api/packs/:packId/monitoring-window — amb month matrix
  // - `monthly(packId, span)`    → GET /api/packs/:packId/monthly — amb month series
  // `all` is the blanket invalidation prefix for byPack+daily+window+monthly; overview
  // lives under its own prefix so create/delete mutations invalidate both.
  rounds: {
    all: ['rounds'],
    overview: (filters) => ['rounds-overview', filters],
    overviewAll: ['rounds-overview'],
    byPack: (packId) => ['rounds', 'byPack', packId],
    daily: (packId, roundId) => ['rounds', 'daily', packId, roundId],
    window: (packId, win) => ['rounds', 'window', packId, win],
    // MON-AMB-UX §3.6.4 — under the `rounds` prefix on purpose: the existing
    // blanket invalidations (queryKeys.rounds.all) already cover it, and no
    // mutation in this phase writes it.
    monthly: (packId, span) => ['rounds', 'monthly', packId, span],
  },

  outreach: {
    campaigns: {
      all: ['outreach', 'campaigns'],
      list: (filters) => ['outreach', 'campaigns', 'list', filters],
      detail: (id) => ['outreach', 'campaigns', 'detail', id],
    },
    contactLists: {
      all: ['outreach', 'contactLists'],
      list: (filters) => ['outreach', 'contactLists', 'list', filters],
      detail: (id) => ['outreach', 'contactLists', 'detail', id],
    },
    templates: {
      all: ['outreach', 'templates'],
      list: () => ['outreach', 'templates', 'list'],
    },
    // Phase 97.8 W2: queryKeys.outreach.instantly subtree removed (Instantly cutover).
  },

  stats: {
    all: ['stats'],
    me: (period) => ['stats', 'me', period],
    funnels: (params) => ['stats', 'funnels', params],
  },

  team: {
    all: ['team'],
    stats: (period) => ['team', 'stats', period],
    funnels: (period) => ['team', 'funnels', period],
    managerSettings: (id) => ['team', 'manager-settings', id],
  },

  goals: {
    all: ['goals'],
    byUser: (userId) => ['goals', userId ?? 'self'],
  },
};
